/**
 * Бумажные поля: то, что рвётся, трескается и горит.
 */

import { boxBlur, fbm, mix, normalize, scatter, valueNoise } from '../core/noise'
import type { FieldFn } from './types'

/**
 * Рваный край.
 *
 * Кромка идёт поперёк элемента, а её положение по каждой строке задаётся
 * одномерным фрактальным смещением. Лестница амплитуд — не подбор на глаз:
 * это спектр реальной трещины в бумаге, где каждая следующая гармоника
 * вчетверо чаще и втрое слабее.
 *
 * Три разных характера из одной механики:
 *   машинная резка — только первая гармоника, край почти прямой;
 *   рваный вручную — вся лестница;
 *   деккельный    — середина глаже, зато перо шире: это не разрыв,
 *                   а натёк бумажной массы под рамкой.
 */
export const torn: FieldFn = ({ w, h, rnd }) => {
  // Направление обрыва из сида, а не зашито вниз: иначе все переходы
  // в приложении рвутся в одну сторону и это читается мгновенно.
  const vertical = rnd() > 0.5
  const flip = rnd() > 0.5
  const along = vertical ? h : w
  const across = vertical ? w : h

  const kind = rnd()
  const amps = kind < 0.25 ? [0.3, 0, 0, 0] : kind < 0.75 ? [7, 2.6, 1, 0.42] : [4.5, 1.2, 0.7, 0.3]
  const freqs = [4, 11, 30, 80]
  const phases = [rnd(), rnd(), rnd(), rnd()]

  // Смещение кромки считается один раз на строку — отсюда и дешевизна:
  // меньше ста тысяч операций на весь механизм.
  const edge = new Float32Array(along)
  for (let i = 0; i < along; i++) {
    const t = i / along
    let d = 0
    for (let o = 0; o < 4; o++) {
      d += Math.sin((t * freqs[o]! + phases[o]!) * Math.PI * 2) * amps[o]!
    }
    edge[i] = d
  }

  const out = new Float32Array(w * h)
  const span = across * 1.06
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = vertical ? y : x
      const j = vertical ? x : y
      const pos = flip ? across - j : j
      out[y * w + x] = (pos + edge[i]!) / span
    }
  }

  const fibres = boxBlur(valueNoise(w, h, 24, rnd), w, h, 1)
  return boxBlur(normalize(mix(normalize(out), fibres, 0.05)), w, h, 1)
}

/**
 * Кракелюр: рост от трещин.
 *
 * Единственное поле с ЖЁСТКИМИ ПРЯМЫМИ РЁБРАМИ — всё остальное здесь круглое
 * или волокнистое. Читается как залом бумаги, растрескавшийся лак, сухая
 * глина.
 *
 * Трещины — это места, где два ближайших центра равноудалены, то есть рёбра
 * диаграммы Вороного. Поле = расстояние до ребра, поэтому раскрытие идёт
 * ОТ трещин наружу и остаётся монотонным: сеть не растёт, растут её берега.
 *
 * Метрика — самая недооценённая ручка: евклид даёт осколки со скруглением,
 * Чебышёв — прямоугольные плитки, манхэттен — ромбы. Одна механика,
 * три разных материала.
 */
export const crackle: FieldFn = ({ w, h, rnd }) => {
  const count = 10 + Math.floor(rnd() * 18)
  const pts = scatter(count, rnd, 0.02)
  const metric = Math.floor(rnd() * 3)

  const dist = (dx: number, dy: number): number => {
    const ax = Math.abs(dx)
    const ay = Math.abs(dy)
    if (metric === 1) return Math.max(ax, ay)
    if (metric === 2) return ax + ay
    return Math.hypot(ax, ay)
  }

  const out = new Float32Array(w * h)
  const aspect = h / w
  for (let y = 0; y < h; y++) {
    const fy = y / h
    for (let x = 0; x < w; x++) {
      const fx = x / w
      let f1 = Infinity
      let f2 = Infinity
      for (const p of pts) {
        const d = dist(fx - p.x, (fy - p.y) * aspect)
        if (d < f1) {
          f2 = f1
          f1 = d
        } else if (d < f2) {
          f2 = d
        }
      }
      // Разность двух ближайших: ноль ровно на ребре между ячейками.
      out[y * w + x] = f2 - f1
    }
  }

  return boxBlur(normalize(out), w, h, 1)
}

/**
 * Прогорание.
 *
 * Самое дорогое и самое красивое. Фронт горения бумаги — не абстрактный
 * шум: медленное тление относится к классу KPZ, откуда два следствия,
 * которые и отличают «горит» от «растворяется по шуму».
 *
 * Первое: затухание октав 0.71, а не привычные 0.5. Фронт получается
 * заметно грубее, с рваными языками вместо мягких волн.
 *
 * Второе: ширина фронта растёт со временем как корень кубический. Мы это
 * подделываем, усиливая высокие частоты по мере удаления от очага —
 * у начала пожара кромка ровнее, чем у края листа.
 *
 * Плюс несколько «лавин»: локальных провалов, где огонь ушёл вперёд рывком.
 * Настоящие фронты именно так себя и ведут, и без них горение выглядит
 * слишком послушным.
 */
export const burn: FieldFn = ({ w, h, rnd }) => {
  const rough = fbm(w, h, rnd, { octaves: 4, cells: 2.5, gain: 0.71, lacunarity: 2 })

  const ox = rnd() > 0.5 ? 0.05 + rnd() * 0.2 : 0.75 + rnd() * 0.2
  const oy = 0.1 + rnd() * 0.8

  const out = new Float32Array(w * h)
  const aspect = h / w
  for (let y = 0; y < h; y++) {
    const dy = (y / h - oy) * aspect
    for (let x = 0; x < w; x++) {
      const dx = x / w - ox
      const r = Math.min(1, Math.hypot(dx, dy) / 1.15)
      // Шероховатость растёт с удалением от очага: t^(1/3).
      const width = 0.1 + 0.34 * Math.cbrt(r)
      out[y * w + x] = r * (1 - width) + rough[y * w + x]! * width
    }
  }

  for (const a of scatter(3 + Math.floor(rnd() * 4), rnd, 0.15)) {
    const radius = 0.06 + rnd() * 0.09
    const depth = 0.05 + rnd() * 0.07
    for (let y = 0; y < h; y++) {
      const dy = (y / h - a.y) * aspect
      for (let x = 0; x < w; x++) {
        const d = Math.hypot(x / w - a.x, dy)
        if (d < radius) out[y * w + x]! -= depth * (1 - d / radius)
      }
    }
  }

  return boxBlur(normalize(out), w, h, 2)
}
